import React from 'react';
import { Avatar } from '@framework-controls/core';
import * as css from './AppointmentCard.module.scss';
import { Appointment, Transportation } from 'src/models';
import { strings } from 'src/localization';
import { ViewMode } from 'src/enums';
import { DateTime } from '@reynolds/datetime';
import classNames from 'classnames';
import NoProfile from 'src/content/icons/Profile2.svg';
import { IconProfile2 } from 'src/content/icons';
import moment from 'moment';
import 'moment/min/locales.min';

export interface IProps {
    advisorPhotoPath: string;
    appointment: Appointment;
    advImgAltTag: string;
    viewMode?: ViewMode;
    theme: string;
    transIndex?: Transportation;
    locale: string;
    modifyAppointment?: () => void;
}

const AppointmentCard = (props: IProps) => {
    const appointment: Appointment = props.appointment;
    const dateValue = new DateTime(new Date(props.appointment.date));
    const displayDate = dateValue.format('DD, YYYY');
    const displayTime = props.appointment.time;
    const acctMode = props.viewMode === ViewMode.CustAcct;
    const editApptMode = props.viewMode === ViewMode.EditAppt;

    const processingDate = dateValue.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dayOfWeekLit = processingUTCDate.format('dddd');
    const MonthLit = processingUTCDate.format('MMMM');

    const flexBoxClass = classNames(
        css.flexBox,
        {
            [css.flexBoxAcct]: acctMode,
            [css.flexBoxMini]: props.theme === 'mini',
            [css.flexBoxCadillac]: props.theme === 'cadillac',
            [css.flexBoxADA]: props.theme === 'colorSensitive' || props.theme === 'lexus',
        },
    );
    const dateRowClass = classNames(
        css.dateRow,
        {
            [css.dateRowAcct]: acctMode,
            [css.dateRowKia]: props.theme === 'kia',
            [css.dateRowHonda]: props.theme === 'honda' || props.theme === 'mercedesBenz',
            [css.dateRowBMW]: props.theme === 'bmw',
            [css.dateRowFord]: props.theme === 'ford',
            [css.dateRowIN]: props.theme === 'infiniti',
            [css.dateRowNissan]: props.theme === 'nissan',
            [css.dateRowMazda]: props.theme === 'mazda',
            [css.dateRowGenesis]: props.theme === 'genesis',
            [css.dateRowAcura]: props.theme === 'acura',
        },
    );
    // PMR 1462985 requested that we always underline the link
    const linkButtonClass = classNames(
        css.summaryEditLinkADA,
        {
            [css.summaryEditLinkBMW]: props.theme === 'bmw',
            [css.summaryEditLinkChevrolet]: props.theme === 'chevrolet',
            [css.summaryEditLinkIN]: props.theme === 'infiniti',
            [css.summaryEditLinkAcura]: props.theme === 'acura',
        },
    );

    const summaryTitleClassName = classNames(
        css.summaryTitle,
        {
            [css.summaryTitleAudi]: props.theme === 'audi',
            [css.summaryTitleHonda]: props.theme === 'honda',
            [css.summaryTitleBMW]: props.theme === 'bmw',
            [css.summaryTitleFord]: props.theme === 'ford',
            [css.summaryTitleIN]: props.theme === 'infiniti',
            [css.summaryTitleMazda]: props.theme === 'mazda',
            [css.summaryTitleGenesis]: props.theme === 'genesis',
            [css.summaryTitleAcura]: props.theme === 'acura',
        },
    );

    const dateRowAStyles = classNames(
        css.dateRowA,
        {
            [css.dateRowABMW]: props.theme === 'bmw',
            [css.dateRowAFord]: props.theme === 'ford',
            [css.dateRowANissan]: props.theme === 'nissan',
        },
    );
    const image = (props.advisorPhotoPath !== '' || props.advisorPhotoPath !== undefined) ? props.advisorPhotoPath : '';
    let apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate + ' | ' + displayTime;

    let header = (<h4 className={summaryTitleClassName}>{strings.Appointment}</h4>);
    if (acctMode) {
        header = (<div className={dateRowAStyles} id='dateTime'>{apptDate}</div>);
    } else if (editApptMode && props.modifyAppointment) {
        header = (
            <div style={{display: 'flex'}}>
                <h4 className={summaryTitleClassName}>{strings.Appointment}</h4>
                <div onClick={props.modifyAppointment} className={linkButtonClass}>{strings.ModifyAppointment}</div>
            </div>
        );
    }

    let lastLine = props.transIndex?.title;
    if (props.appointment.transFlag === 'N') {
        apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate;
        lastLine = strings.DropOff + displayTime;
    }
    const transRowClass = classNames (
        css.transRow,
        {
            [css.transRowLexus]: (props.theme === 'lexus'),
            [css.transRowHonda]: (props.theme === 'honda' || props.theme === 'mercedesBenz'),
            [css.transRowBMW]: (props.theme === 'bmw'),
            [css.transRowFord]: (props.theme === 'ford'),
            [css.transRowIN]: props.theme === 'infiniti',
        },
    );

    const advRowClass = classNames (
        css.advRow,
        {
            [css.advRowCV]: (props.theme === 'chevrolet'),
            [css.advRowBMW]: (props.theme === 'bmw'),
            [css.advRowFord]: (props.theme === 'ford'),
            [css.advRowIN]: props.theme === 'infiniti',
            [css.advRowNissan]: (props.theme === 'nissan'),
            [css.advRowHonda]: props.theme === 'honda' || props.theme === 'mercedesBenz',
            [css.advRowMazda]: (props.theme === 'mazda'),
            [css.advRowGenesis]: (props.theme === 'genesis'),
            [css.advRowAcura]: (props.theme === 'acura'),
        },
    );

    let avatarColor: 'default' | 'primary' | 'secondary' | 'contrast' | 'completed' | 'blank' = 'primary';
    if (props.theme === 'mercedesBenz') {
        avatarColor = 'contrast';
    }

    const apptIcon = classNames (
        css.apptIcon,
        /* {
            [css.apptIconMB]: (props.theme === 'mercedesBenz'),
        }, */
    );

    const withDivStyles = classNames (
        css.WithDiv,
        {
            [css.WithDivBMW]: (props.theme === 'bmw'),
            [css.WithDivFord]: (props.theme === 'ford'),
            [css.WithDivNissan]: (props.theme === 'nissan'),
            [css.WithDivIN]: props.theme === 'infiniti',
        },
    );

    const renderAvatar = () => {
        if (image) {
            return (
                <Avatar
                    src={image}
                    elevation={0}
                    size={60}
                    color='blank'
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        if (acctMode && (props.theme === 'kia' || props.theme === 'nissan')) {
            return (
                <Avatar
                    src={NoProfile}
                    elevation={0}
                    size={60}
                    color={avatarColor}
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        return (
            <div className={`${props.theme === 'lexusMonogram' ? `${css.apptIconLexusMonogram}` : `${apptIcon}`}`}>
                <IconProfile2
                    style={{
                        height: '60px',
                        width: '60px',
                    }}
                />
            </div>
        );
    };

    return (
        <div>
            {header}
            <div className={css.summaryCard + ' ' + flexBoxClass}>
                {renderAvatar()}
                <div className={css.appointmentData}>
                    {acctMode ?
                        (<div className={withDivStyles}>{strings.With + ' '}</div>) : null
                    }
                    <div className={dateRowClass} id='dateTime'>{apptDate}</div>
                    <div className={advRowClass} id='advisor'>{appointment.advName}</div>
                    <div className={transRowClass} id='transportation'>{lastLine}</div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentCard;
